import { type JobStatusFilterTypes } from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import { JobPickerFilterTypes } from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import {
    type IJobPickerPersistantState,
    JobIdTypes,
} from "commonComponents/utilities/JobPicker/JobPicker.types";

import { type SelectedFilterItem } from "entity/filters/Filter/Filter.api.types";
import { type SavedFilterItem } from "entity/filters/SavedFilter/SavedFilter.api.types";
import { JobStatus } from "entity/job/Job.api.types";

/** Minimal shape needed for General Job filtering logic */
export interface IGeneralJobFilterable {
    jobId: number;
    jobStatus: JobStatus;
}

export interface IGeneralJobFilterResult<T extends IGeneralJobFilterable> {
    filteredJobs: T[];
    isGeneralJobFallback: boolean;
}

/**
 * Filters the General job from job picker results based on whether the user has other accessible jobs.
 * When the user has real (non-General, non-placeholder) jobs, the General job is removed.
 * When the General job is the only real job available, it is kept and flagged as a fallback.
 *
 * @param jobs - The jobs returned from the job picker API
 * @param includeGeneralJob - Whether General job filtering should be applied
 * @returns The filtered jobs array and whether the General job is being used as a fallback
 */
export function filterGeneralJob<T extends IGeneralJobFilterable>(
    jobs: T[],
    includeGeneralJob: boolean,
): IGeneralJobFilterResult<T> {
    if (!includeGeneralJob) {
        return { filteredJobs: jobs, isGeneralJobFallback: false };
    }

    const realNonGeneralJobs = jobs.filter(
        (j) => j.jobId > 0 && j.jobStatus !== JobStatus.GeneralJob,
    );

    if (realNonGeneralJobs.length > 0) {
        return {
            filteredJobs: jobs.filter((j) => j.jobStatus !== JobStatus.GeneralJob),
            isGeneralJobFallback: false,
        };
    }

    const isGeneralJobFallback = jobs.some((j) => j.jobStatus === JobStatus.GeneralJob);
    return { filteredJobs: jobs, isGeneralJobFallback };
}

/**
 * Determines the selected job ID when the current job is not found in the job list.
 * Falls back to AllJobs for owner portal users, when the General job is the only option,
 * or when the page opts in via defaultToAllJobs; otherwise falls back to NoJobs.
 *
 * @param isOwnerPortal - Whether the current portal is the owner portal
 * @param isGeneralJobFallback - Whether the General job is being used as a fallback
 * @param defaultToAllJobs - Whether the page wants AllJobs as its no-job default
 * @returns The fallback job ID
 */
export function getFallbackSelectedJobId(
    isOwnerPortal: boolean,
    isGeneralJobFallback: boolean,
    defaultToAllJobs: boolean = false,
): number {
    if (isOwnerPortal || isGeneralJobFallback || defaultToAllJobs) {
        return JobIdTypes.AllJobs;
    }
    return JobIdTypes.NoJobs;
}

/**
 * Stale-while-revalidate predicate for the JobPicker loading overlay.
 * Returns true (= show overlay) only on the very first picker load when no
 * data exists yet. Subsequent reloads keep the existing picker rendered and
 * swap in fresh data on completion, avoiding a visible BTLoading flash.
 *
 * Used by `ListPageLayout.handleJobChange`. Extracted so the predicate can be
 * tested in isolation — the surrounding handler is hard to drive in a unit
 * test because it depends on a populated `jobPickerHandler`, builder context,
 * and full picker state.
 */
export function isJobPickerInitialLoad(jobPickerData: unknown): boolean {
    return jobPickerData === undefined;
}

export function getStoredJobPickerFilterString(
    templatesOnly: boolean,
    jobPickerState: Pick<IJobPickerPersistantState, "filters" | "templateFilters">,
    savedFilters: Pick<SavedFilterItem, "isDefault" | "value">[],
): string {
    const storedFilter = templatesOnly ? jobPickerState.templateFilters : jobPickerState.filters;
    const defaultFilter = savedFilters.find((x) => x.isDefault);
    return storedFilter || defaultFilter?.value || "";
}

/**
 * The job status used to sync the legacy session. An empty selection keeps the previous value
 * rather than sending no status, which the server would treat as every job.
 */
export function getSyncedJobStatusFilter(
    selectedFilters: SelectedFilterItem[] | undefined,
    current: JobStatusFilterTypes[],
): JobStatusFilterTypes[] {
    const selectedJobStatus = selectedFilters?.find(
        (item) => item.key === (JobPickerFilterTypes.Other as string),
    )?.selectedValue;
    if (Array.isArray(selectedJobStatus) && selectedJobStatus.length > 0) {
        return [...(selectedJobStatus as JobStatusFilterTypes[])];
    }
    return current;
}

/**
 * The status list after selecting a job. A job whose status the selection filters out has that
 * status added so it stays listed; an empty selection already lists every status, so it is left
 * alone. Always returns a new array: the status list is shared with the selected filters.
 */
export function addJobStatusToSelection(
    selectedStatuses: JobStatusFilterTypes[],
    jobStatus: JobStatusFilterTypes,
): JobStatusFilterTypes[] {
    if (selectedStatuses.length === 0 || selectedStatuses.includes(jobStatus)) {
        return [...selectedStatuses];
    }
    return [...selectedStatuses, jobStatus];
}
